/**
 * Pantalla de actualización de email
 * Migrada desde la app móvil con React Query y arquitectura moderna
 */

import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { FiShield, FiArrowLeft } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useCurrentUserQuery, useUpdateEmailMutation } from '../../hooks/useAuthMutations';
import { validateEmailForm, getAuthErrorMessage } from '../../utils/validation';
import PasswordVerificationScreen from './PasswordVerificationScreen';

/**
 * Componente principal para actualizar el email del usuario
 */
const UpdateEmailScreen = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Estados locales
  const [newEmail, setNewEmail] = useState('');
  const [isPasswordVerified, setIsPasswordVerified] = useState(false);

  // Hooks de TanStack Query
  const { data: userData, isLoading: isLoadingUser, error: userError } = useCurrentUserQuery();
  const updateEmailMutation = useUpdateEmailMutation();

  // Efecto para manejar errores en la carga del usuario
  useEffect(() => {
    if (userError) {
      console.error('Error cargando usuario:', userError);
      toast.error(
        'Sesión caducada. Por seguridad cerramos la sesión.'
      );
    }
  }, [userError]);

  /**
   * Maneja la verificación exitosa de la contraseña
   */
  const handlePasswordVerified = () => {
    setIsPasswordVerified(true);
  };

  /**
   * Valida y ejecuta el cambio de email
   */
  const handleUpdateEmail = async (e) => {
    e.preventDefault();
    
    try {
      // Validar formulario
      const validation = validateEmailForm(newEmail, userData?.email);
      if (!validation.isValid) {
        toast.error(validation.message);
        return;
      }

      // Actualizar email
      await updateEmailMutation.mutateAsync(newEmail);

      // Mostrar mensaje de éxito
      toast.success(
        'Mail de confirmación enviado. Si no está en tu bandeja de entrada, revisá tu carpeta de spam.'
      );

      // Regresar a la pantalla anterior
      navigate(-1);

    } catch (error) {
      console.error('Error actualizando email:', error);
      
      const errorMessage = getAuthErrorMessage(error);
      toast.error(errorMessage);
      
      // Limpiar el campo en caso de error
      setNewEmail('');
    }
  };

  // Pantalla de carga mientras se obtiene el usuario
  if (isLoadingUser) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-white">Cargando...</div>
      </div>
    );
  }

  // Si no se ha verificado la contraseña, mostrar pantalla de verificación
  if (!isPasswordVerified) {
    return (
      <PasswordVerificationScreen
        title="Verifica tu contraseña"
        subtitle="Para continuar, vuelve a introducir tu contraseña de Amarsi."
        onSuccess={handlePasswordVerified}
        successMessage="Contraseña verificada correctamente"
        buttonText="Continuar"
      />
    );
  }

  // Renderizar contenido principal
  return (
    <div className="min-h-screen bg-gray-900 p-4">
      <div className="max-w-2xl mx-auto">
        {/* Header con botón de retroceso */}
        <div className="flex items-center mb-6">
          <button
            onClick={() => navigate(-1)}
            className="text-gray-400 hover:text-white transition-colors mr-4"
            disabled={updateEmailMutation.isLoading}
          >
            <FiArrowLeft className="h-6 w-6" />
          </button>
          <h1 className="text-2xl font-bold text-white">
            Cambiar correo electrónico
          </h1>
        </div>

        {/* Card principal */}
        <div className="bg-gray-800 rounded-lg shadow-xl p-6">
          {/* Header informativo */}
          <div className="flex items-start mb-6 p-4 bg-gray-700 rounded-lg">
            <FiShield className="h-6 w-6 text-violet-400 mr-3 mt-1 flex-shrink-0" />
            <div className="text-gray-300 text-sm">
              <p className="mb-2">Enviaremos un email de confirmación al nuevo email.</p>
              <p>Solo al confirmarse, se actualizarán los cambios.</p>
            </div>
          </div>

          {/* Formulario */}
          <form onSubmit={handleUpdateEmail} className="space-y-6">
            {/* Campo de entrada para nuevo email */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Nuevo correo electrónico
              </label>
              <input
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                placeholder="ejemplo@correo.com"
                autoFocus
                disabled={updateEmailMutation.isLoading}
              />
            </div>

            {/* Email actual del usuario */}
            <div className="p-4 bg-gray-700 rounded-lg">
              <label className="block text-sm font-medium text-gray-400 mb-1">
                Email actual
              </label>
              <p className="text-gray-300">
                {userData?.email || user?.email || 'N/A'}
              </p>
            </div>

            {/* Indicadores de validación */}
            {newEmail && (
              <div>
                {!validateEmailForm(newEmail, userData?.email).isValid && (
                  <p className="text-red-400 text-sm">
                    {validateEmailForm(newEmail, userData?.email).message}
                  </p>
                )}
              </div>
            )}

            {/* Botón de actualización */}
            <button
              type="submit"
              disabled={
                !validateEmailForm(newEmail, userData?.email).isValid || 
                updateEmailMutation.isLoading
              }
              className="w-full bg-violet-600 hover:bg-violet-700 text-white font-medium py-3 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {updateEmailMutation.isLoading ? (
                <>
                  <svg className="animate-spin h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Procesando...
                </>
              ) : (
                'Cambiar correo'
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default UpdateEmailScreen;
